import { CountryDropdown } from "react-country-region-selector";
import { useTranslation } from "react-i18next";
import { countries } from "countries-list";
import "./formField.scss";
import PropTypes from "prop-types";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Dropdown from "../Dropdown";
import SearchableDropdown from "../SearchableDropdown";
import DatePicker from "react-datepicker";
import HideIcon from "../../images/icons/hide.png";
import ShowIcon from "../../images/icons/show.png";

function FormField({
  value,
  onChange,
  error,
  label,
  name,
  dropDownValues,
  showPassword,
  togglePassword,
  type,
  placeholder,
  noOptionsMessage,
  secondaryTheme,
  optionRender,
}) {
  const { t } = useTranslation("fieldForm");
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);

  const getCountryInfo = (countryName) => {
    const countryData = Object.entries(countries).find(
      ([key, country]) => country.name.toLowerCase() === countryName.toLowerCase(),
    );

    if (countryData) {
      const [cca2, country] = countryData;
      onChange({
        name: country.name,
        callingCode: country.phone,
        cca2,
        currency: country.currency,
      });
    } else {
      onChange({ name: countryName });
    }
  };

  const renderInput = () => {
    const inputClassName = error ? "errorInput" : "";

    switch (type) {
      case "password":
        return (
          <div className="passwordField">
            <input
              name={name}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              required
              className={`form-control ${inputClassName}`}
              type={showPassword ? "text" : "password"}
              placeholder={placeholder}
            />
            <button type="button" onClick={togglePassword}>
              <img className="passwordFieldImage" src={showPassword ? HideIcon : ShowIcon} />
            </button>
          </div>
        );

      case "country":
        return (
          <CountryDropdown
            value={value?.name || ""}
            onChange={getCountryInfo}
            className={`form-control ${inputClassName}`}
            defaultOptionLabel={t("selectCountry")}
          />
        );

      case "dropdown":
        return (
          <Dropdown
            options={dropDownValues}
            value={dropDownValues.find((option) => option.value === value) || null}
            onChange={(selectedOption) => onChange(selectedOption.value)}
          />
        );

      case "searchableDropdown":
        return (
          <SearchableDropdown
            options={dropDownValues}
            value={dropDownValues.find((option) => option.value === value) || null}
            onChange={(selectedOption) => onChange(selectedOption?.value || "")}
            placeholder={placeholder}
            noOptionsMessage={noOptionsMessage}
            optionRender={optionRender}
          />
        );

      case "date":
      case "dateTime":
      case "time":
        return (
          <DatePicker
            selected={value ? new Date(value) : null}
            onChange={(date) => onChange(date?.toISOString())}
            showTimeSelect={type !== "date"}
            showTimeSelectOnly={type === "time"}
            timeIntervals={15}
            dateFormat={
              type === "date" ? "dd/MM/yyyy" : type === "dateTime" ? "dd/MM/yyyy HH:mm" : "HH:mm"
            }
            placeholderText={
              type === "date" ? "JJ/MM/AAAA" : type === "dateTime" ? "JJ/MM/AAAA HH:mm" : "HH:mm"
            }
            timeCaption="Heure"
            className={`form-control ${inputClassName}`}
            required
            showPopperArrow={false}
          />
        );
      case "birthDate":
        return (
          <DatePicker
            selected={value ? new Date(value) : null}
            onChange={(date) => onChange(date?.toISOString())}
            showTimeSelect={false} // Disable time selection
            showTimeSelectOnly={false} // Ensure only date selection
            dateFormat="dd/MM/yyyy" // Date format
            placeholderText="JJ/MM/AAAA" // Placeholder text for date
            required
            showPopperArrow={false}
            showYearDropdown={true} // Show year dropdown
            yearDropdownItemNumber={100} // Show 15 years in the dropdown
            scrollableYearDropdown={true} // Make the year dropdown scrollable
            maxDate={maxDate} // Prevent selecting future dates
          />
        );
      case "tel":
        return (
          <div className="phone-input-container">
            <PhoneInput
              placeholder="Entrez votre numéro"
              defaultCountry="BE"
              international
              smartCaret={false}
              value={value}
              onChange={onChange}
              className="phone-input"
            />
          </div>
        );

      case "text":
        return (
          <input
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required
            className={`form-control ${inputClassName}`}
            type={type}
            placeholder={placeholder}
          />
        );

      case "longText":
        return (
          <textarea
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required
            className={`form-control ${inputClassName}`}
            type={type}
            placeholder={placeholder}
          />
        );

      default:
        return (
          <input
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required
            className={`form-control ${inputClassName}`}
            type={type}
            placeholder={placeholder}
          />
        );
    }
  };

  return (
    <div className={`fieldGroup ${secondaryTheme ? "secondaryTheme" : ""}`}>
      {label && <label htmlFor={name}>{label}</label>}
      {renderInput()}
      {error && <p className="errorMessage">{error}</p>}
    </div>
  );
}

FormField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  dropDownValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  showPassword: PropTypes.bool,
  togglePassword: PropTypes.func,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  optionRender: PropTypes.func,
  prefixesTel: PropTypes.arrayOf(PropTypes.string),
  secondaryTheme: PropTypes.bool,
};

FormField.defaultProps = {
  prefixesTel: [],
  type: "text",
};

export default FormField;
